import { ReactNode } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface SideDrawerHeaderProps {
  title: string | React.ReactNode;
  onClose: () => void;
  dataTestId?: string;
  icon?: ReactNode;
  isMobileView?: boolean;
  showDivider?: boolean;
  actions?: ReactNode;
}

const SideDrawerHeader = ({
  title,
  onClose,
  dataTestId = 'sideDrawer-header',
  icon = null,
  isMobileView = false,
  showDivider = true,
  actions = null,
}: SideDrawerHeaderProps) => {
  const titleWidth = isMobileView ? '90%' : '528px';
  const hasHeaderActions = Boolean(actions);

  const titleStack = (
    <Stack
      direction={isMobileView ? 'row' : 'column'}
      spacing={1}
      alignItems="center"
      sx={{
        minWidth: 0,
        flex: hasHeaderActions ? { xs: '1 1 auto', sm: 1 } : 1,
      }}
    >
      {icon}
      <Typography
        variant="h3"
        fontSize="18px !important"
        width={titleWidth}
        data-testid="sideDrawer-header-title"
      >
        {title}
      </Typography>
    </Stack>
  );

  const closeButton = (
    <IconButton
      onClick={onClose}
      aria-label="close"
      data-testid="sideDrawer-header-close"
      sx={{
        flexShrink: 0,
        ...(hasHeaderActions && {
          order: { xs: 2, sm: 3 },
        }),
      }}
    >
      <CloseIcon />
    </IconButton>
  );

  const actionsSlot = hasHeaderActions ? (
    <Stack
      direction="row"
      justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
      data-testid="sideDrawer-header-actions"
      sx={{
        width: { xs: '100%', sm: 'auto' },
        order: { xs: 3, sm: 2 },
        '& > *': { width: { xs: '100%', sm: 'auto' } },
        mx: 2,
      }}
    >
      {actions}
    </Stack>
  ) : null;

  return (
    <>
      <Stack
        direction="row"
        flexWrap={hasHeaderActions ? 'wrap' : 'nowrap'}
        alignItems="center"
        justifyContent="space-between"
        data-testid={dataTestId}
        spacing={{ sm: 1 }}
        sx={{ gap: 1 }}
      >
        {titleStack}

        {hasHeaderActions ? (
          <>
            {closeButton}
            {actionsSlot}
          </>
        ) : (
          closeButton
        )}
      </Stack>
      {showDivider && <Divider sx={{ mt: 2 }} />}
    </>
  );
};

export default SideDrawerHeader;
